import React, { useEffect, useState, useCallback } from 'react';
import {
  Grid,
  Box,
  Chip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Edit } from '@mui/icons-material';
import LoadingBackdrop from 'ui-component/full-screen-loadder/LoadingBackdrop';
import MainCard from 'ui-component/cards/MainCard';
import CustomNoRowsOverlay from 'ui-component/customNoRowsOverlay';
import SearchBar from 'ui-component/SearchBar/SearchBar';
import ActionButtons from 'ui-component/actionButton/ActionButtons';
import toast from 'react-hot-toast';
import calculatorService from '../../axios/calculatorService';
import EditCalculatorCategoryModal from 'ui-component/Modals/EditCalculatorCategoryModal';

const CalculatorsTable = () => {
  const [calculators, setCalculators] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedCalculator, setSelectedCalculator] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10
  });
  const [sortModel, setSortModel] = useState([]);
  const [rowCount, setRowCount] = useState(0);

  const sortCalculatorField = (field) => {
    switch (field) {
      case 'calculatorName':
        return 'CalculatorName';

      case 'categoryName':
        return 'CalculatorCategory.CategoryName';

      default:
        return 'CreatedDate';
    }
  };

  // Fetch calculators
  const fetchCalculators = useCallback(async () => {
    try {
      setLoading(true);

      const sortField = sortModel.length > 0 ? sortCalculatorField(sortModel[0].field) : 'CreatedDate';

      const sortOrder =
        sortModel.length > 0
          ? sortModel[0].sort === 'asc'
            ? 'Ascending'
            : 'Descending'
          : 'Descending';

      const filter = search ? `CalculatorName~LIKE~${search}` : undefined;

      const response = await calculatorService.getAllCalculators({
        top: paginationModel.pageSize,
        skip: paginationModel.page * paginationModel.pageSize,
        filter,
        orderby: sortField,
        orderbydirection: sortOrder
      });

      const data = response?.values || [];
      const formatted = data.map((item) => ({
        id: item.id || item.Id,
        calculatorName: item.calculatorName || item.CalculatorName || '',
        categoryId: item.categoryId || '',
        categoryName: item.categoryName || 'Uncategorized',
        isActive: item.isActive ?? true,
        color: item.color || 'gray',
        iconUrl: item.iconUrl || '',
      }));
      setCalculators(formatted);
      setRowCount(response?.totalCount || 0);
    } catch (err) {
      console.error('Error fetching calculators:', err);
      setCalculators([]);
    } finally {
      setLoading(false);
    }
  }, [paginationModel, sortModel, search]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await calculatorService.getAllCategories();
      setCategories(response || []);
    } catch (err) {
      console.error('Background category fetch failed', err);
    }
  }, []);

  useEffect(() => {
    fetchCalculators();
  }, [fetchCalculators]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCategories();
    }, 0);

    return () => clearTimeout(timer);
  }, [fetchCategories])


  const handleSearchChange = (event) => {
    setSearch(event.target.value);
  };

  // Handle isActive toggle
  const handleIsActiveChange = async (row) => {
    try {
      setLoading(true);

      // Optimistic update
      setCalculators(prevCalcs =>
        prevCalcs.map(c => ({
          ...c,
          isActive: c.id === row.id ? !row.isActive : c.isActive
        }))
      );

      await calculatorService.updateCalculator(row.id, {
        isActive: !row.isActive
      })

      toast.success('Calculator status updated successfully');
    } catch (err) {
      console.error('Failed to update accessible status', err);
      toast.error('Failed to update calculator status');
      setCalculators(prevCalcs =>
        prevCalcs.map(c => ({
          ...c,
          isActive: c.id === row.id ? row.isActive : c.isActive
        }))
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle edit category
  const handleEdit = useCallback((row) => {
    setSelectedCalculator(row);
    setOpenEditModal(true);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setOpenEditModal(false);
    setSelectedCalculator(null);
  }, []);

  const handleSaveCategory = async (calculatorId, categoryId) => {
    try {
      await calculatorService.updateCalculator(calculatorId, { categoryId })
      // Update local state
      const selectedCategory = categories.find(cat => cat.id === categoryId);
      setCalculators(prevCalcs =>
        prevCalcs.map(c =>
          c.id === calculatorId
            ? { ...c, categoryId, categoryName: selectedCategory?.name || 'Uncategorized', color: selectedCategory?.color }
            : c
        )
      );
      toast.success('Category updated successfully');
    } catch (err) {
      console.error('Failed to update category', err);
      toast.error('Failed to update category');
      throw err;
    }
  };

  const handleUploadIcon = async (calculatorId, file) => {
    try {
      setLoading(true);
      const response = await calculatorService.uploadCalculatorIcon(calculatorId, file);

      const baseIconUrl = response;
      
      // Cache-bust so the browser loads the fresh image instead of a cached one
      const newIconUrl = baseIconUrl
        ? `${baseIconUrl}${baseIconUrl.includes('?') ? '&' : '?'}v=${Date.now()}`
        : '';

      console.log("data url", baseIconUrl, "cache-busted", newIconUrl);

      if (baseIconUrl) {
        setCalculators((prevCalcs) =>
          prevCalcs.map((c) =>
            c.id === calculatorId ? { ...c, iconUrl: newIconUrl } : c
          )
        );
      }

      toast.success('Icon uploaded successfully');
    } catch (err) {
      console.error('Failed to upload icon', err);
      toast.error('Failed to upload icon');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Table columns
  const columns = [
    {
      field: 'icon',
      headerName: 'Icon',
      width: 80,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box
          sx={{
            height: '100%',
            width: '100%',
            display: 'flex',
            alignItems: 'center', 
            justifyContent: 'center',
          }}
        >
          {params.row.iconUrl ? (
            <Box
              component="img"
              src={params.row.iconUrl}
              alt={params.row.calculatorName}
              sx={{ width: 30, height: 30, objectFit: 'contain' }}
            />
          ) : (
            <span style={{ fontSize: 10, color: '#999' }}>No icon</span>
          )}
        </Box>
      )
    },
    { field: 'calculatorName', headerName: 'Calculator Name', flex: 1 },
    {
      field: 'categoryName',
      headerName: 'Category',
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            backgroundColor: `#${params.row.color}`,
            color: '#fff',
          }}
        />
      )
    },
    {
      field: 'isActive',
      headerName: 'Is Active',
      width: 120,
      renderCell: (params) => (
        <input
          type="checkbox"
          checked={params.value || false}
          onChange={() => handleIsActiveChange(params.row)}
          style={{ transform: 'scale(1.2)', cursor: 'pointer' }}
        />
      )
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <ActionButtons
          actions={[
            {
              tooltip: 'Edit Category',
              icon: <Edit />,
              color: 'primary',
              onClick: () => handleEdit(params.row)
            }
          ]}
        />
      )
    }
  ];

  return (
    <>
      <MainCard title="Calculators Management">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <SearchBar
              value={search}
              onChange={handleSearchChange}
              placeholder="Search calculators"
            />
          </Grid>

          <Grid item xs={12}>
            <DataGrid
              rows={calculators}
              columns={columns}
              rowCount={rowCount}
              paginationMode="server"
              disableColumnSorting
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              sortModel={sortModel}
              onSortModelChange={setSortModel}
              pageSizeOptions={[5, 10, 20, 50]}
              getRowId={(row) => row.id}
              disableRowSelectionOnClick
              slots={{ noRowsOverlay: CustomNoRowsOverlay }}
              autoHeight
              sx={{
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#f5f5f5',
                  fontWeight: 'bold'
                }
              }}
            />
          </Grid>
        </Grid>
      </MainCard>

      {selectedCalculator && (
        <EditCalculatorCategoryModal
          open={openEditModal}
          onClose={handleCloseEditModal}
          calculator={selectedCalculator}
          categories={categories}
          onSave={handleSaveCategory}
          onUploadIcon={handleUploadIcon}
        />
      )}

      <LoadingBackdrop open={loading} />
    </>
  );
};

export default CalculatorsTable;